import { useEffect, useState } from 'react';
import { FileSpreadsheet, Download } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import PageHeader from '../../../components/common/PageHeader';
import EmptyState from '../../../components/common/EmptyState';
import StatusChip from '../components/shared/StatusChip';
import LoadingSkeleton from '../components/shared/LoadingSkeleton';
import PAMMErrorState from '../components/shared/PAMMErrorState';
import { DataTable } from '../../../components/ui/data-table';
import { pammStatementService } from '../services/pammService';
import { formatCurrency, getProfitColor, exportToCSV } from '../utils';
import type { PAMMStatement } from '../types';
import type { DataTableColumn } from '../../../components/ui/data-table';
import toast from 'react-hot-toast';

export default function Statements() {
  const [statements, setStatements] = useState<PAMMStatement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [activeFilters, setActiveFilters] = useState<{ [key: string]: string }>(
    {}
  );

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      setStatements(await pammStatementService.getAll());
    } catch {
      setError('Failed to load statements.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const handleDownload = (stmt: PAMMStatement) => {
    toast.success(
      `Downloading statement for ${stmt.investorName} — ${stmt.period}`
    );
  };

  const columns: DataTableColumn<PAMMStatement>[] = [
    {
      id: 'investor',
      header: 'Investor',
      accessor: row => (
        <div>
          <p className="font-medium text-xs">{row.investorName}</p>
        </div>
      ),
      sortable: true,
      sortValue: row => row.investorName,
    },
    {
      id: 'fund',
      header: 'Fund',
      accessor: row => <span className="text-xs">{row.fundName}</span>,
    },
    {
      id: 'period',
      header: 'Period',
      accessor: row => <span className="text-xs">{row.period}</span>,
    },
    {
      id: 'opening',
      header: 'Opening Balance',
      accessor: row => (
        <span className="text-xs">{formatCurrency(row.openingBalance)}</span>
      ),
      sortable: true,
      sortValue: row => row.openingBalance,
    },
    {
      id: 'deposits',
      header: 'Deposits',
      accessor: row => (
        <span className="text-xs text-green-600">
          +{formatCurrency(row.deposits)}
        </span>
      ),
    },
    {
      id: 'withdrawals',
      header: 'Withdrawals',
      accessor: row => (
        <span className="text-xs text-red-500">
          -{formatCurrency(row.withdrawals)}
        </span>
      ),
    },
    {
      id: 'profit',
      header: 'Profit',
      accessor: row => (
        <span className={`text-xs font-semibold ${getProfitColor(row.profit)}`}>
          {formatCurrency(row.profit)}
        </span>
      ),
      sortable: true,
      sortValue: row => row.profit,
    },
    {
      id: 'fees',
      header: 'Fees',
      accessor: row => (
        <span className="text-xs text-muted-foreground">
          -{formatCurrency(row.fees)}
        </span>
      ),
    },
    {
      id: 'closing',
      header: 'Closing Balance',
      accessor: row => (
        <span className="text-xs font-semibold">
          {formatCurrency(row.closingBalance)}
        </span>
      ),
      sortable: true,
      sortValue: row => row.closingBalance,
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => <StatusChip status={row.status} size="sm" />,
    },
    {
      id: 'actions',
      header: '',
      accessor: row =>
        row.status === 'ready' ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-blue-600 hover:bg-blue-50"
            onClick={() => handleDownload(row)}
          >
            <Download className="h-3.5 w-3.5" />
          </Button>
        ) : null,
    },
  ];

  if (loading)
    return (
      <div className="space-y-4">
        <PageHeader title="Statements" />
        <LoadingSkeleton type="table" />
      </div>
    );
  if (error)
    return (
      <div className="space-y-4">
        <PageHeader title="Statements" />
        <PAMMErrorState message={error} onRetry={load} />
      </div>
    );

  return (
    <div className="space-y-6 pb-6">
      <PageHeader
        title="Statements"
        description="Investor account statements with full financial summaries"
      />
      {statements.length === 0 ? (
        <EmptyState
          icon={FileSpreadsheet}
          title="No Statements"
          description="Investor account statements will appear here."
        />
      ) : (
        <DataTable
          columns={columns}
          data={statements}
          searchValue={searchValue}
          searchPlaceholder="Search statements..."
          onSearchChange={setSearchValue}
          onSearchReset={() => setSearchValue('')}
          activeFilters={activeFilters}
          onFiltersChange={setActiveFilters}
          showExportButton={true}
          exportButtonText="Export CSV"
          onExportClick={() => exportToCSV(statements, 'pamm_statements')}
        />
      )}
    </div>
  );
}
